import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface StaffHiringProps {
    onBack: () => void;
}

interface JobOpening {
    id: string;
    title: string;
    department: string;
    location: string;
    salaryRange: string;
    description: string;
    requirements: string[];
    preferred: string[];
    status: 'Open' | 'Interviewing' | 'Filled' | 'On Hold';
    datePosted: string;
    priority: 'High' | 'Medium' | 'Low';
}

interface Candidate {
    id: string;
    name: string;
    role: string;
    email: string;
    phone: string;
    currentCompany: string;
    resumeUrl: string;
    stage: 'Applied' | 'Screening' | 'Interview' | 'Technical' | 'Offer' | 'Hired' | 'Rejected';
    interviewScore: number;
    notes: string;
    appliedDate: string;
}

const StaffHiring: React.FC<StaffHiringProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'openings' | 'candidates' | 'process'>('openings');

    const jobOpenings: JobOpening[] = [
        {
            id: 'job-1',
            title: 'Senior React Developer',
            department: 'Engineering',
            location: 'New York, NY (Hybrid)',
            salaryRange: '$130K - $160K',
            description: 'Lead frontend development for our energy retrofit platform using React, TypeScript, and Tailwind CSS.',
            requirements: [
                '5+ years of React experience',
                'TypeScript proficiency',
                'Experience with data visualization libraries',
                'Experience with REST API integration',
                'Building production-grade web applications'
            ],
            preferred: [
                'Energy sector experience',
                'Experience with building management systems',
                'GraphQL knowledge',
                'AWS or cloud platform experience'
            ],
            status: 'Interviewing',
            datePosted: '2025-04-15',
            priority: 'High'
        },
        {
            id: 'job-2',
            title: 'Data Scientist',
            department: 'Analytics',
            location: 'New York, NY (Remote Option)',
            salaryRange: '$120K - $150K',
            description: 'Develop models to predict building energy usage and retrofit ROI based on NYC building energy data.',
            requirements: [
                'MS/PhD in Data Science, Statistics, or related field',
                'Python and data analysis libraries (Pandas, NumPy, scikit-learn)',
                'Experience with energy data analysis',
                'Strong statistical background',
                'Data visualization skills'
            ],
            preferred: [
                'Experience with NYC Local Law 97',
                'Building energy modeling',
                'Experience with energy efficiency analysis',
                'LEED or energy certification knowledge'
            ],
            status: 'Open',
            datePosted: '2025-05-01',
            priority: 'Medium'
        },
        {
            id: 'job-3',
            title: 'Business Development Manager',
            department: 'Sales',
            location: 'New York, NY',
            salaryRange: '$110K - $140K + Commission',
            description: 'Drive partnerships with property owners, management companies, and contractors to grow our retrofit platform.',
            requirements: [
                '4+ years in B2B sales or business development',
                'Experience in real estate, energy, or related industries',
                'Strong network in NYC real estate',
                'Track record of closing enterprise deals',
                'CRM experience'
            ],
            preferred: [
                'Experience with sustainability or green building initiatives',
                'Knowledge of NYC Local Law 97',
                'Commercial real estate background',
                'Energy efficiency or retrofit project experience'
            ],
            status: 'Open',
            datePosted: '2025-05-10',
            priority: 'High'
        },
        {
            id: 'job-4',
            title: 'Marketing Manager',
            department: 'Marketing',
            location: 'New York, NY (Remote Option)',
            salaryRange: '$90K - $115K',
            description: 'Lead marketing efforts for our energy retrofit platform targeting NYC building owners and property managers.',
            requirements: [
                '3+ years in B2B marketing',
                'Content marketing experience',
                'Digital campaign management',
                'Analytics and performance tracking',
                'Email marketing experience'
            ],
            preferred: [
                'Real estate or proptech marketing experience',
                'Sustainability or cleantech background',
                'Event planning experience',
                'SEO/SEM knowledge'
            ],
            status: 'Open',
            datePosted: '2025-05-12',
            priority: 'Medium'
        },
        {
            id: 'job-5',
            title: 'Backend Engineer',
            department: 'Engineering',
            location: 'New York, NY (Hybrid)',
            salaryRange: '$125K - $155K',
            description: 'Develop and maintain APIs and data processing pipelines for our energy retrofit platform.',
            requirements: [
                'Strong Node.js experience',
                'Database design and optimization',
                'API development',
                'Experience with cloud platforms (AWS/Azure/GCP)',
                'Data processing at scale'
            ],
            preferred: [
                'TypeScript knowledge',
                'Experience with time-series data',
                'Building management system integration',
                'IoT platform experience'
            ],
            status: 'Interviewing',
            datePosted: '2025-04-20',
            priority: 'High'
        }
    ];

    const candidates: Candidate[] = [
        {
            id: 'cand-1',
            name: 'Michael Johnson',
            role: 'Senior React Developer',
            email: 'michael.j@example.com',
            phone: '(212) 555-1234',
            currentCompany: 'TechCorp Inc.',
            resumeUrl: '/resumes/michael-johnson.pdf',
            stage: 'Technical',
            interviewScore: 4.7,
            notes: 'Strong technical skills, good culture fit. Technical assessment was excellent.',
            appliedDate: '2025-04-20'
        },
        {
            id: 'cand-2',
            name: 'Sarah Chen',
            role: 'Senior React Developer',
            email: 'sarah.c@example.com',
            phone: '(212) 555-5678',
            currentCompany: 'InnovateTech',
            resumeUrl: '/resumes/sarah-chen.pdf',
            stage: 'Interview',
            interviewScore: 4.2,
            notes: 'Good technical skills, excellent communication. Scheduling technical assessment.',
            appliedDate: '2025-04-22'
        },
        {
            id: 'cand-3',
            name: 'David Rodriguez',
            role: 'Data Scientist',
            email: 'david.r@example.com',
            phone: '(212) 555-9012',
            currentCompany: 'DataMetrics LLC',
            resumeUrl: '/resumes/david-rodriguez.pdf',
            stage: 'Applied',
            interviewScore: 0,
            notes: 'Strong background in energy data analysis. PhD from Columbia.',
            appliedDate: '2025-05-05'
        },
        {
            id: 'cand-4',
            name: 'Emma Wilson',
            role: 'Business Development Manager',
            email: 'emma.w@example.com',
            phone: '(212) 555-3456',
            currentCompany: 'RealEstate Partners',
            resumeUrl: '/resumes/emma-wilson.pdf',
            stage: 'Screening',
            interviewScore: 3.8,
            notes: 'Strong network in NYC real estate. Previously at CBRE.',
            appliedDate: '2025-05-12'
        },
        {
            id: 'cand-5',
            name: 'Alex Patel',
            role: 'Backend Engineer',
            email: 'alex.p@example.com',
            phone: '(212) 555-7890',
            currentCompany: 'CloudSoft Systems',
            resumeUrl: '/resumes/alex-patel.pdf',
            stage: 'Offer',
            interviewScore: 4.8,
            notes: 'Excellent candidate, all interviews passed with flying colors. Preparing offer letter.',
            appliedDate: '2025-04-25'
        }
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Open': return 'bg-green-100 text-green-800';
            case 'Interviewing': return 'bg-blue-100 text-blue-800';
            case 'Filled': return 'bg-gray-100 text-gray-800';
            case 'On Hold': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStageColor = (stage: string) => {
        switch (stage) {
            case 'Applied': return 'bg-gray-100 text-gray-800';
            case 'Screening': return 'bg-blue-100 text-blue-800';
            case 'Interview': return 'bg-purple-100 text-purple-800';
            case 'Technical': return 'bg-yellow-100 text-yellow-800';
            case 'Offer': return 'bg-orange-100 text-orange-800';
            case 'Hired': return 'bg-green-100 text-green-800';
            case 'Rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };
    
    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'High': return 'bg-red-100 text-red-800';
            case 'Medium': return 'bg-yellow-100 text-yellow-800';
            case 'Low': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Staff Hiring</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        Manage job openings, candidates, and the hiring process
                    </p>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <p className="text-indigo-800 text-sm">
                            <strong>🚀 Hiring Priority:</strong> We're scaling our team to support platform growth.
                            Focus on engineering and business development roles first.
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('openings')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'openings'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📋 Job Openings
                            </button>
                            <button
                                onClick={() => setActiveTab('candidates')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'candidates'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                👥 Candidates
                            </button>
                            <button
                                onClick={() => setActiveTab('process')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'process'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🔄 Hiring Process
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* Job Openings Tab */}
                        {activeTab === 'openings' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">Current Job Openings</h2>
                                    <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                        + Add New Position
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    {jobOpenings.map((job, idx) => (
                                        <div key={idx} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                                                            {job.status}
                                                        </span>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(job.priority)}`}>
                                                            {job.priority} Priority
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-600">{job.department} • {job.location}</p>
                                                    <p className="text-green-600 font-medium mt-1">{job.salaryRange}</p>
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Posted: {job.datePosted}
                                                </div>
                                            </div>
                                            
                                            <p className="text-gray-700 mb-4">{job.description}</p>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                                                <div>
                                                    <h4 className="font-medium text-gray-900 mb-2">Requirements</h4>
                                                    <ul className="space-y-1">
                                                        {job.requirements.map((req, i) => (
                                                            <li key={i} className="flex items-start">
                                                                <span className="text-blue-500 mr-2">•</span>
                                                                <span className="text-sm text-gray-700">{req}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-gray-900 mb-2">Preferred Qualifications</h4>
                                                    <ul className="space-y-1">
                                                        {job.preferred.map((pref, i) => (
                                                            <li key={i} className="flex items-start">
                                                                <span className="text-green-500 mr-2">+</span>
                                                                <span className="text-sm text-gray-700">{pref}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                            
                                            <div className="flex space-x-2">
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    View Applicants
                                                </button>
                                                <button className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Edit Position
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Candidates Tab */}
                        {activeTab === 'candidates' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Candidate Pipeline</h2>
                                
                                <div className="space-y-6">
                                    {candidates.map((candidate, idx) => (
                                        <div key={idx} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{candidate.name}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(candidate.stage)}`}>
                                                            {candidate.stage}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-900 font-medium">{candidate.role}</p>
                                                    <p className="text-gray-600">{candidate.currentCompany}</p>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm text-gray-500">
                                                        Applied: {candidate.appliedDate}
                                                    </div>
                                                    {candidate.interviewScore > 0 && (
                                                        <div className="mt-1">
                                                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                                                                Score: {candidate.interviewScore}/5
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Contact:</span>
                                                    <p className="text-sm">
                                                        <span className="text-gray-700">{candidate.email}</span>
                                                        <br />
                                                        <span className="text-gray-700">{candidate.phone}</span>
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Documents:</span>
                                                    <p className="text-sm">
                                                        <a href={candidate.resumeUrl} className="text-blue-600 hover:underline">Resume</a>
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="bg-gray-100 rounded-lg p-3 mb-4">
                                                <p className="text-sm text-gray-700">{candidate.notes}</p>
                                            </div>
                                            
                                            <div className="flex space-x-2">
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Move to Next Stage
                                                </button>
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Schedule Interview
                                                </button>
                                                <button className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Add Notes
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Process Tab */}
                        {activeTab === 'process' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Hiring Process</h2>
                                
                                <div className="bg-white rounded-lg p-6 border border-gray-200 mb-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Standard Interview Process</h3>
                                    <div className="space-y-6">
                                        <div className="flex items-start">
                                            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">1</div>
                                            <div>
                                                <h4 className="text-lg font-semibold mb-2">Application Review</h4>
                                                <p className="text-gray-700 mb-2">Initial screening of resume and application materials.</p>
                                                <ul className="text-sm text-gray-600 space-y-1">
                                                    <li>• Check for required skills and experience</li>
                                                    <li>• Evaluate relevant industry experience</li>
                                                    <li>• Review portfolio/projects if applicable</li>
                                                </ul>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start">
                                            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">2</div>
                                            <div>
                                                <h4 className="text-lg font-semibold mb-2">Phone Screening (30 min)</h4>
                                                <p className="text-gray-700 mb-2">Initial call to assess basic fit and interest.</p>
                                                <ul className="text-sm text-gray-600 space-y-1">
                                                    <li>• Confirm details from resume</li>
                                                    <li>• Discuss salary expectations and timeline</li>
                                                    <li>• Evaluate communication skills</li>
                                                    <li>• Assess interest and understanding of our mission</li>
                                                </ul>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start">
                                            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">3</div>
                                            <div>
                                                <h4 className="text-lg font-semibold mb-2">Technical Assessment</h4>
                                                <p className="text-gray-700 mb-2">Role-specific technical evaluation.</p>
                                                <ul className="text-sm text-gray-600 space-y-1">
                                                    <li>• Engineering: Coding challenge or take-home project</li>
                                                    <li>• Data Science: Analysis exercise with sample data</li>
                                                    <li>• Business/Sales: Case study or presentation</li>
                                                    <li>• Marketing: Campaign strategy or content sample</li>
                                                </ul>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start">
                                            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">4</div>
                                            <div>
                                                <h4 className="text-lg font-semibold mb-2">Team Interview (1 hour)</h4>
                                                <p className="text-gray-700 mb-2">Interview with potential team members and peers.</p>
                                                <ul className="text-sm text-gray-600 space-y-1">
                                                    <li>• Deep dive into technical skills and experience</li>
                                                    <li>• Collaborative problem-solving exercises</li>
                                                    <li>• Team fit and working style assessment</li>
                                                    <li>• Questions from candidate about the role and team</li>
                                                </ul>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start">
                                            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">5</div>
                                            <div>
                                                <h4 className="text-lg font-semibold mb-2">Final Interview</h4>
                                                <p className="text-gray-700 mb-2">Meeting with department head and/or leadership.</p>
                                                <ul className="text-sm text-gray-600 space-y-1">
                                                    <li>• Higher-level discussion about career goals</li>
                                                    <li>• Cultural fit and company values alignment</li>
                                                    <li>• Strategic thinking and vision</li>
                                                    <li>• Final questions and clarifications</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="bg-blue-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Hiring Stats</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Open Positions:</span>
                                                <span className="font-semibold">{jobOpenings.filter(j => j.status === 'Open' || j.status === 'Interviewing').length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Active Candidates:</span>
                                                <span className="font-semibold">{candidates.length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Interviews This Week:</span>
                                                <span className="font-semibold">7</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Offers Extended:</span>
                                                <span className="font-semibold">{candidates.filter(c => c.stage === 'Offer').length}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Average Time to Hire:</span>
                                                <span className="font-semibold">24 days</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-green-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Onboarding Process</h3>
                                        <ul className="space-y-2">
                                            <li className="flex items-start">
                                                <span className="text-green-500 mr-2">✓</span>
                                                <div>
                                                    <span className="font-medium">Pre-start documentation</span>
                                                    <p className="text-sm text-gray-600">Offer letter, contract, equipment selection</p>
                                                </div>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-green-500 mr-2">✓</span>
                                                <div>
                                                    <span className="font-medium">First day orientation</span>
                                                    <p className="text-sm text-gray-600">Company overview, team intros, systems access</p>
                                                </div>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-green-500 mr-2">✓</span>
                                                <div>
                                                    <span className="font-medium">Week 1: Platform training</span>
                                                    <p className="text-sm text-gray-600">Technical onboarding, codebase review, tooling setup</p>
                                                </div>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-green-500 mr-2">✓</span>
                                                <div>
                                                    <span className="font-medium">30/60/90 day plan</span>
                                                    <p className="text-sm text-gray-600">Role-specific goals, expectations, and checkpoints</p>
                                                </div>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="text-green-500 mr-2">✓</span>
                                                <div>
                                                    <span className="font-medium">Buddy program</span>
                                                    <p className="text-sm text-gray-600">Pairing with experienced team member for questions and guidance</p>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Link
                        to="/cto-recruitment"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">👨‍💻</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">CTO Recruitment</h3>
                            <p className="text-sm text-gray-600">Executive leadership search</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/real-estate-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🏢</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Real Estate Partners</h3>
                            <p className="text-sm text-gray-600">Property companies and agents</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/company-management"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">📊</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Company Overview</h3>
                            <p className="text-sm text-gray-600">Organization management</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default StaffHiring;
